// 结算落地（EP-007-S1 / GDD 02 §②-2.3 / ADR-004 §2.3）。
//
// **唯一**可以写入 `lastSettledTripId` 的地方（CI 守门 check-settle-ownership）。
// 全工程其它位置（store / tick / UI）出现对该字段的赋值即违规。
//
// 顺序（先入库后关闸门，避免「已归来却无礼物」竞态，见 store.tick 注释）：
//   1. 把 dropResult.giftIds 入库（upsertStacks，同 id 自动合并 count）。
//   2. tripCount + 1、isFirstTripDone = true。
//   3. 写 lastSettledTripId = trip.id（★唯一写入点）。
//   4. dogState = 'home'、trip = null（关闸门）。
//
// 幂等（GDD 02 C3 / ADR-004 §2.3）：同一 trip 重复调用直接返回原档，
// 离线回归最多结算一次，杜绝重复入库。
//
// 🆕 STEP 5-B：明信片**不再**在 settleTrip 抽 —— 改为 tick 在 t=0.5（progress >= 0.5）
//   首次跨越时一次性 roll（详见 store.tick）。SettleInput 不再接受 rng。
//
// 本文件在 src/domain，不得调用 Date.now / Math.random；时间由入参 `input.now` 注入。
import type { SaveDataV1 } from '../save/types';
import { upsertStacks } from './inventory/upsertStacks';

export interface SettleInput {
  now: number;
}

export function pureSettle(save: SaveDataV1, input: SettleInput): SaveDataV1 {
  const trip = save.trip;

  // 守卫 1：没有在途 trip —— 无法结算，原样返回（保护，不抛错）。
  if (!trip) return save;
  // 守卫 2：尚未进入 awaitingReveal —— 不能结算，原样返回。
  if (trip.status !== 'awaitingReveal') return save;
  // 守卫 3：该 trip 已结算过（离线回归最多一次）—— 直接返回，幂等。
  if (save.lastSettledTripId === trip.id) return save;

  // 守卫 4：dropResult 为空（异常态）—— 警告并回退为空礼物，仍关闸门、不丢其它状态。
  // （不在此重抽：避免引入随机源；先关闸门保证不重复入库，符合「先入库后关闸门」死锁约束。）
  let giftIds: string[];
  if (!trip.dropResult) {
    console.warn('[pureSettle] trip.dropResult 为 null，回退为空礼物入库（trip=' + trip.id + '）');
    giftIds = [];
  } else {
    giftIds = trip.dropResult.giftIds;
  }

  // 🆕 P6-POLISH-ENG-002 §B.4：本次是否空手，决定存档里的 lastReturnWasEmpty。
  //   判定依据：`src/domain/drop/roll.ts` 的红线保证正常掉落 giftIds 恒 ≥ 1，
  //   所以「有 dropResult 且 giftIds 为空」⇔ 由 planReturn 判定的空手归来。
  //   dropResult 为 null 是**异常态**（守卫 4 已 warn），那时不算空手 —— 不能让玩家替异常买单。
  const emptyHanded = trip.dropResult !== null && giftIds.length === 0;

  const inventory = upsertStacks(save.inventory, giftIds, input.now);
  const newTripCount = save.stats.tripCount + 1;

  // 🆕 GDD 01 §9.5：反重复保护窗口写入（与 lastReturnWasEmpty 同套路：additive + 条件 spread）。
  //   规则：giftIds.length > 0 才 push（带礼物的回归推进窗口），
  //   空手 / 异常态（giftIds 为空）不推进 —— 与 §9.6 E6/E7 一致。
  //   FIFO：slice(-antiRepeatWindowSize) 由 settle 唯一写入点把控（不动运行时其它模块的窗口大小）。
  //   读取侧统一 `?? []`，键不存在与空数组语义一致。
  //   exactOptionalPropertyTypes 下不能用"无条件写 recentTripDrops"（会污染空手的存档多出一个 key），
  //   所以走 `...(cond ? { recentTripDrops: ... } : {})` 模式 —— 与 lastReturnWasEmpty 逐字对齐。
  const nextRecentTripDrops =
    giftIds.length > 0
      ? [...(save.recentTripDrops ?? []), giftIds].slice(-3)
      : undefined;

  // 一次性对象字面量构造（顺序固定：inventory → stats → lastSettledTripId → dogState → trip）。
  // mailbox / lastPostcardNoteIdByDestination 由 tick 在 t=0.5 触发，已在 settle 之前完成。
  // ⚠️ lastReturnWasEmpty 是唯一的「是否空手」真相源写入点 —— 下次结算时被 planReturn 读取，
  //    用于禁止连续两次空手。与 lastSettledTripId 一样，别在别处赋值。
  // ⚠️ recentTripDrops 是唯一的「反重复窗口」真相源写入点 —— 下次 roll 时被 planReturn 透传给 roll，
  //    用于抽样前的权重调整。与 lastSettledTripId 一样，别在别处赋值（CI check-settle-ownership 扩字段）。
  return {
    ...save,
    inventory,
    stats: { tripCount: newTripCount, isFirstTripDone: true },
    lastSettledTripId: trip.id, // ★ 全项目唯一写入点
    lastReturnWasEmpty: emptyHanded,
    ...(nextRecentTripDrops ? { recentTripDrops: nextRecentTripDrops } : {}),
    dogState: 'home',
    trip: null,
  };
}
